import {
  Badge,
  Box,
  Code,
  Text,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  BadgeProps,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function ScheduledTaskRuns({
  isOpen,
  onClose,
  taskName,
}: {
  isOpen: boolean;
  onClose: () => void;
  taskName?: string;
}): React.ReactElement {
  if (!isOpen) {
    return <></>;
  }

  if (typeof taskName === 'string') {
    return <ScheduledTaskRunsList taskName={taskName} isOpen={isOpen} onClose={onClose} />;
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <DrawerBody>
          <Alert status="warning">
            <AlertIcon />
            <Text>Please select a scheduled task to view its runs.</Text>
          </Alert>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export function ScheduledTaskRunsList({
  isOpen,
  onClose,
  taskName,
}: {
  isOpen: boolean;
  onClose: () => void;
  taskName: string;
}): React.ReactElement {
  const { data } = trpcAdmin.scheduledTasks.listRuns.useQuery({
    taskName: decodeURIComponent(taskName),
  });

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader style={{ padding: '0px' }}>
          <DrawerCloseButton onClick={onClose} />
        </DrawerHeader>
        <Box p="4">
          <Text fontSize="xl" fontWeight="bold">
            Scheduled Task Runs <Code>{decodeURIComponent(taskName)}</Code>
          </Text>
        </Box>

        <DrawerBody p="4" border="none" m="0">
          {typeof data !== 'undefined' &&
          'items' in data &&
          Array.isArray(data.items) &&
          data.items.length > 0 ? (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Started At</Th>
                  <Th>Duration</Th>
                  <Th width="50%">Message</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((run) => {
                  let statusColor: BadgeProps['colorScheme'];

                  switch (run?.status) {
                    case 'COMPLETED':
                      statusColor = 'green';
                      break;
                    case 'RUNNING':
                      statusColor = 'blue';
                      break;
                    case 'FAILED':
                      statusColor = 'red';
                      break;
                    default:
                      statusColor = 'gray';
                  }

                  return (
                    <Tr key={run.id}>
                      <Td>
                        <Badge colorScheme={statusColor}>{run.status}</Badge>
                      </Td>
                      <Td>{run.startedAt.toLocaleString()}</Td>
                      <Td>{run.durationMs ? `${run.durationMs}ms` : '-'}</Td>
                      <Td maxWidth="400px">
                        {run.errorMessage ? (
                          <Code fontSize="sm" color="red.600" wordBreak="break-word">
                            {run.errorMessage}
                          </Code>
                        ) : run.resultMessage ? (
                          <Code fontSize="sm" color="green.600" wordBreak="break-word">
                            {run.resultMessage}
                          </Code>
                        ) : (
                          '-'
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          ) : (
            <Alert status="info">
              <AlertIcon />
              <Text>
                No scheduled task runs found for <Code>{decodeURIComponent(taskName)}</Code>.
              </Text>
            </Alert>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
